/**
 * A lista do aviso de vencimento, com os módulos que a pessoa pode ver.
 *
 * Papel: aqui fica `useItensVencendo`, que junta os documentos, os planos de
 * saúde e as rotinas preventivas já apurados, e diz se as consultas deles já
 * responderam. É a mesma lista para o sino (`BotaoDeVencimentos`) da barra
 * superior e para a caixa (`PopupVencimentosDaAbertura`) que o sino e a
 * abertura da página mostram.
 *
 * Como usar: quem precisa da lista chama o hook, e não os três hooks de
 * módulo por conta própria. O acesso é conferido aqui, por `acessoAlcanca` com
 * nível `'ver'` em `documentos`, `saude` e `manutencoes-boa-vista`: cada linha
 * nomeia a pessoa ou a casa e o registro, e uma lista montada sem essa
 * conferência vazaria, por cima da grade de permissões, o que a grade existe
 * para fechar. Um aviso que juntasse os módulos de outro jeito passaria a
 * mostrar, no sino e na caixa, listas diferentes.
 *
 * As três consultas são sempre montadas, e é o interruptor de cada hook de
 * módulo que decide se ela vai à API: hook não se chama condicionalmente, e
 * quem não tem um dos módulos receberia 403 a cada carregamento. Como o
 * TanStack Query guarda a consulta pela chave, o sino e a caixa leem o mesmo
 * cache, e montar os dois não dobra nenhuma requisição.
 *
 * A consulta que falha entra como lista vazia: o aviso é acessório, e a falha
 * de um módulo não deverá esconder as linhas dos outros.
 */
import { acessoAlcanca } from '@concierge/shared';

import { apurarVencimentosDeDocumentos, useDocumentos } from '@/modules/documentos';
import { apurarVencimentosDePreventivas, usePreventivas } from '@/modules/manutencoes';
import { apurarVencimentosDePlanos, usePlanosSaude } from '@/modules/saude';
import { useAcesso } from '@/shared/hooks';
import { type ItemVencendo, hojeEmIso } from '@/shared/lib/vencimento';

/** O que `useItensVencendo` devolve. */
export interface ItensVencendo {
  /** Os itens dos módulos liberados, sem ordem; quem desenha é que ordena. */
  itens: ItemVencendo[];
  /**
   * Verdadeiro quando o acesso já é conhecido e toda consulta ligada já
   * respondeu, com dado ou com falha.
   *
   * Continua falso enquanto o acesso é consultado — sem ele, nenhuma consulta
   * está ligada, e uma lista vazia pareceria a resposta final — e enquanto
   * qualquer das consultas ligadas ainda espera, inclusive nas novas
   * tentativas depois de uma falha.
   */
  respondido: boolean;
  /** Se a pessoa tem `ver` em `documentos`, e por isso pode abrir aquela tela. */
  podeVerDocumentos: boolean;
}

/**
 * Monta a lista do aviso de vencimento.
 *
 * Quem chama são o `BotaoDeVencimentos`, para a contagem, e o
 * `PopupVencimentosDaAbertura`, para a caixa; os dois recebem os mesmos itens,
 * apurados com a data de hoje de `hojeEmIso`.
 *
 * @returns Os itens dos módulos que a pessoa pode ver, se as consultas já
 *   responderam, e se a tela de documentos está liberada para ela.
 */
export function useItensVencendo(): ItensVencendo {
  const acesso = useAcesso();
  const podeVerDocumentos =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'documentos', 'ver');
  const podeVerSaude = acesso.data !== undefined && acessoAlcanca(acesso.data, 'saude', 'ver');
  const podeVerManutencoes =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'manutencoes-boa-vista', 'ver');

  const documentos = useDocumentos(podeVerDocumentos);
  const planos = usePlanosSaude(podeVerSaude);
  const preventivas = usePreventivas(podeVerManutencoes);

  const hoje = hojeEmIso();
  const itens = [
    ...apurarVencimentosDeDocumentos(documentos.data ?? [], hoje),
    ...apurarVencimentosDePlanos(planos.data ?? [], hoje),
    ...apurarVencimentosDePreventivas(preventivas.data ?? [], hoje),
  ];

  // Consulta desligada fica em `isPending` para sempre, e por isso só as
  // ligadas contam: esperar por uma desligada deixaria `respondido` falso para
  // quem não tem aquele módulo.
  const respondido =
    acesso.data !== undefined &&
    (!podeVerDocumentos || !documentos.isPending) &&
    (!podeVerSaude || !planos.isPending) &&
    (!podeVerManutencoes || !preventivas.isPending);

  return { itens, respondido, podeVerDocumentos };
}
